import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useEditingMode } from '../../useEditingMode';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useCustomizationContext } from '../../useCardCustomization';
import { useSwitchDraftProduct } from './useSwitchDraftProduct';
import { getDimensions } from '@apps/card/utils';
import { isPostcard } from '@apps/card/utils/cardSizeName';
import { useIsDigitalPostcardEnabled } from '@apps/card/hooks/useIsDigitalPostcardEnabled';

type UseProductChoiceGroupArgs = {
  draftId: string;
};

/**
 * card_service refuses `updateStationeryDraftProduct` with this message while the draft has an
 * order in the cart. Matched on text because the API carries no error code for it.
 */
const isCartLockError = (error: unknown) => error instanceof Error && /order is in progress/i.test(error.message);

export const useProductChoiceGroup = ({ draftId }: UseProductChoiceGroupArgs) => {
  const [themeId] = useCustomizationContext(d => d.template.themeId);
  const [templateCategory] = useCustomizationContext(d => d.stationeryTemplateCategory);
  const [card] = useCustomizationContext(d => d.config.card);
  const { products, activeProduct } = useStationeryProductPricingContext();
  const { mode } = useEditingMode();
  const { customizerProductChoiceGroupClicked } = useCardTelemetry();
  const { switchProduct, isSwitching } = useSwitchDraftProduct({ draftId });
  const { tq } = useTranslation('stationery');
  const { toast } = useToast();
  const { getValueString } = useQueryParamHelper();

  // Editing a cart item keeps the customizer in draft mode (so the draft saver runs) and
  // signals the cart order only through the `order` query param -- see `UpdateCartBanner`.
  // The backend refuses to change a draft's format while it has an order in the cart
  // ("Cannot change format while an order is in progress"), so the switch is shown locked
  // with a hint rather than offered as a control that can never work.
  const isLockedToCartItem = !!getValueString('order');

  // Product types a draft can switch to via updateStationeryDraftProduct
  // (card_service's resolveTargetProductConfig allowlist). Magnet is
  // print-format like the postcard product on the same templates — any two of
  // these on one template make the toggle meaningful (a postcard + magnet
  // template has no digital sibling, e.g.).
  //
  // The free Basic Digital V2 product is never a target: switching to it means putting the premium
  // design back as well, which this hook's save-switch-navigate path does not do. A draft that has
  // left free returns only through the upgrade's own undo -- the digital editor's upsell dialog.
  const isSwitchTarget = (productType: string) => productType === 'stationeryCard' || productType === 'digitalCard' || productType === 'magnet';

  // A postcard's own Premium Digital stays hidden while `printDigitalPostcards` is off -- the
  // same kill switch the PDP applies (see `useIsDigitalPostcardEnabled`). Until it resolves the target
  // is simply not listed, so a print postcard never flashes a switch it may not be allowed.
  const { width, height } = getDimensions(card);
  const isPostcardDraft = isPostcard(templateCategory, width, height);
  const { isEnabled: isDigitalPostcardEnabled } = useIsDigitalPostcardEnabled({
    hasPostcardDigital: isPostcardDraft && products.some(p => p.productType === 'digitalCard'),
  });
  const isHiddenTarget = (productType: string) => productType === 'digitalCard' && isPostcardDraft && !isDigitalPostcardEnabled;

  const choiceProducts = products.filter(p => p.id === activeProduct.id || (isSwitchTarget(p.productType) && !isHiddenTarget(p.productType)));

  const canSelectProduct = mode === 'draft' && choiceProducts.length >= 2;

  const selectProduct = useEventCallback(async (targetProductId: string) => {
    if (!canSelectProduct || isLockedToCartItem) return;

    const targetProduct = choiceProducts.find(p => p.id === targetProductId);
    if (!targetProduct) return;

    const toTelemetryLabel = (productType: string): 'paper' | 'premiumDigital' | 'basicDigitalV2' | 'magnet' => {
      if (productType === 'magnet') return 'magnet';
      if (productType === 'basicDigitalV2') return 'basicDigitalV2';
      return productType === 'stationeryCard' ? 'paper' : 'premiumDigital';
    };
    const from = toTelemetryLabel(activeProduct.productType);
    const to = toTelemetryLabel(targetProduct.productType);

    try {
      await switchProduct(targetProduct, {
        // Record the switch only once the mutation succeeded — firing earlier
        // counts failed attempts as successful switches.
        onSwitched: () => customizerProductChoiceGroupClicked({ from, to, themeId, draftId }),
      });
    } catch (error) {
      addError(error, { context: 'useProductChoiceGroup', draftId, targetProductId });
      // The group dims while the switch is in flight and snaps back when it fails. Without
      // this the user sees a control that flickers and does nothing.
      //
      // The cart lock can still be hit without `?order=` -- the draft was added to the cart
      // from another tab, say -- so the backend's refusal gets the same hint the locked rows
      // show, not the generic failure.
      const message = isCartLockError(error) ? tq`Remove this item from your cart to change its format.` : tq`We couldn't change the format. Your card wasn't changed.`;
      toast(message, { intent: 'destructive', position: 'bottom-center' });
    }
  });

  return { selectProduct, isSwitching, canSelectProduct, isLockedToCartItem, choiceProducts };
};
